import React from "react";

/**
 * The facts the SCDES Pretreatment Performance Report asks about an industrial
 * user that stay the same from one report to the next. Part of Edit Company.
 */
export const IU_TYPES = [
  "Non-Categorical SIU",
  "CIU",
  "MTCIU",
  "Non-Significant Categorical IU",
  "Zero Discharge CIU",
  "General Permit SIU",
  "Non-Significant Non-Categorical IU",
];
const CATEGORICAL = ["CIU", "MTCIU"];

export type PpsFacts = {
  contact_title: string;
  county: string;
  iu_type: string;
  more_stringent_limits: "" | "yes" | "no";
  cfr_citations: { part: string; subpart: string }[];
  subject_to_local_limits: boolean;
  contributing_jurisdiction: string;
  ca_samples_in_lieu: boolean;
};

export const ppsFactsFrom = (c: any): PpsFacts => ({
  contact_title: c.contact_title ?? "",
  county: c.county ?? "",
  iu_type: c.iu_type ?? "",
  more_stringent_limits: c.more_stringent_limits == null ? "" : c.more_stringent_limits ? "yes" : "no",
  cfr_citations: Array.isArray(c.cfr_citations) ? c.cfr_citations : [],
  subject_to_local_limits: c.subject_to_local_limits !== false,
  contributing_jurisdiction: c.contributing_jurisdiction ?? "",
  ca_samples_in_lieu: !!c.ca_samples_in_lieu,
});

/** The facts as the company update API takes them. */
export const ppsFactsPayload = (f: PpsFacts) => ({
  ...f,
  more_stringent_limits: f.more_stringent_limits === "" ? null : f.more_stringent_limits === "yes",
  cfr_citations: f.cfr_citations.filter(r => r.part.trim() || r.subpart.trim()),
});

export default function CompanyPpsFields({ value, onChange }: { value: PpsFacts; onChange: (v: PpsFacts) => void }) {
  const set = (patch: Partial<PpsFacts>) => onChange({ ...value, ...patch });
  const setCfr = (i: number, patch: Partial<{ part: string; subpart: string }>) =>
    set({ cfr_citations: value.cfr_citations.map((r, k) => (k === i ? { ...r, ...patch } : r)) });
  const categorical = CATEGORICAL.includes(value.iu_type);

  return (
    <fieldset style={st.box}>
      <legend style={st.legend}>SCDES report</legend>
      <label style={st.label}>Contact title</label>
      <input style={st.input} value={value.contact_title} placeholder="e.g. EHS Manager"
        onChange={e => set({ contact_title: e.target.value })} />
      <label style={st.label}>County</label>
      <input style={st.input} value={value.county} onChange={e => set({ county: e.target.value })} />
      <label style={st.label}>IU type</label>
      <select style={st.input} value={value.iu_type} onChange={e => set({ iu_type: e.target.value })}>
        <option value="">— not set —</option>
        {IU_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
      </select>
      {categorical && (
        <>
          <label style={st.label}>Limits as stringent or more stringent than the categorical standards?</label>
          <select style={st.input} value={value.more_stringent_limits}
            onChange={e => set({ more_stringent_limits: e.target.value as PpsFacts["more_stringent_limits"] })}>
            <option value="">— not set —</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
          <label style={st.label}>Categorical CFR citation (one subpart per line)</label>
          {value.cfr_citations.map((r, i) => (
            <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6 }}>
              <input style={{ ...st.input, marginBottom: 0, flex: 2 }} value={r.part} aria-label="CFR part"
                placeholder="433-Metal finishing point source category" onChange={e => setCfr(i, { part: e.target.value })} />
              <input style={{ ...st.input, marginBottom: 0, flex: 1 }} value={r.subpart} aria-label="CFR subpart"
                placeholder="Subpart 15" onChange={e => setCfr(i, { subpart: e.target.value })} />
              <button type="button" style={st.small} aria-label="Remove citation"
                onClick={() => set({ cfr_citations: value.cfr_citations.filter((_, k) => k !== i) })}>✕</button>
            </div>
          ))}
          <button type="button" style={{ ...st.small, marginBottom: 10 }}
            onClick={() => set({ cfr_citations: [...value.cfr_citations, { part: "", subpart: "" }] })}>
            + Add citation
          </button>
        </>
      )}
      <label style={st.check}>
        <input type="checkbox" checked={value.subject_to_local_limits}
          onChange={e => set({ subject_to_local_limits: e.target.checked })} /> Subject to local limits
      </label>
      <label style={st.check}>
        <input type="checkbox" checked={value.ca_samples_in_lieu}
          onChange={e => set({ ca_samples_in_lieu: e.target.checked })} /> The POTW samples in lieu of the IU
      </label>
      <label style={st.label}>Contributing jurisdiction</label>
      <input style={st.input} value={value.contributing_jurisdiction}
        placeholder="leave blank if it discharges to this POTW's own system"
        onChange={e => set({ contributing_jurisdiction: e.target.value })} />
    </fieldset>
  );
}

const st: Record<string, React.CSSProperties> = {
  box: { border: "1px solid #e2e8f0", borderRadius: 6, padding: "8px 12px 4px", margin: "4px 0 12px" },
  legend: { fontSize: 12, fontWeight: 700, color: "#2c5282", padding: "0 4px" },
  label: { display: "block", fontSize: 13, fontWeight: 600, color: "#4a5568", marginBottom: 4 },
  input: { width: "100%", boxSizing: "border-box", padding: "7px 9px", border: "1px solid #cbd5e0",
           borderRadius: 5, fontSize: 13, marginBottom: 8 },
  check: { display: "flex", gap: 6, alignItems: "center", fontSize: 13, color: "#4a5568", marginBottom: 8 },
  small: { padding: "3px 10px", fontSize: 12, background: "#fff", border: "1px solid #cbd5e0", borderRadius: 4,
           cursor: "pointer", color: "#4a5568" },
};
